import { Paragraph, ParagraphOptions } from "@tiptap/extension-paragraph";
import { Node } from "@tiptap/pm/model";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

import setNodeRef from "@/view/ProseMirrorNode/utils/setNodeRef";
import ReadAloudSpans from "@/view/ReadAloudControls/ReadAloudSpans";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode/useReadAloudMode";
import NodeWithTranslation from "@/view/TiptapArticleBody/components/NodeWithTranslation";
import { ComponentPropsWithIdentifier } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";

export interface ParagraphBlockOptions extends ParagraphOptions {
  // useLegacyHighlighting is used to continue converting text to spans while listen mode
  // is on as we have been doing for the article body
  // TODO: LESS-3253 - Remove useLegacyHighlighting and spans
  useLegacyHighlighting?: boolean;
  disableTranslation?: boolean;
}

export const ParagraphComponent = ({
  editor,
  node,
  extension,
}: ComponentPropsWithIdentifier<ParagraphBlockOptions>) => {
  const { isReadAloudModeActive, readAloudSpanRefs } = useReadAloudMode();
  const { disableTranslation = false, useLegacyHighlighting } =
    extension?.options;
  const identifier = `${node.attrs["data-identifier"] || ""}`;
  const text = node.textContent.trim();

  node.identifier = identifier;

  return isReadAloudModeActive && useLegacyHighlighting ? (
    <NodeViewWrapper
      as="p"
      ref={(ref: Node) => setNodeRef(ref, readAloudSpanRefs, node)}
    >
      <ReadAloudSpans startOffsetRelativeToParent={0} text={text} />
    </NodeViewWrapper>
  ) : (
    <NodeWithTranslation
      state={editor.state}
      node={node}
      props={{ "data-identifier": identifier, as: "p" }}
      disableTranslation={disableTranslation}
    />
  );
};

export const ParagraphBlock = Paragraph.extend<ParagraphBlockOptions>({
  addAttributes() {
    return {
      "data-identifier": {
        default: null,
      },
    };
  },
  addOptions() {
    return {
      ...this.parent?.(),
      useLegacyHighlighting: true,
      disableTranslation: false,
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ["p", HTMLAttributes, 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ParagraphComponent, {
      contentDOMElementTag: "p",
    });
  },
});
